/**
 * Input tracker — keyboard plus canvas pointer.
 *
 * Keyboard state is split into *held* (`isDown`) and *edge* presses
 * (`wasPressed`); pointer state likewise tracks the live position and an edge
 * `wasClicked`. Edge state clears once per fixed step via `endStep()`, so an
 * event is reported for exactly one step. Tests drive keys deterministically
 * through `simulateKeyDown` / `simulateKeyUp`.
 */

/** A pointer position in logical canvas pixels. */
export interface PointerPos {
  x: number;
  y: number;
}

/** Convert a raw `KeyboardEvent.key` value into a normalized engine key name. */
export function normalizeKey(raw: string): string {
  const key = raw.toLowerCase();
  return key === ' ' || key === 'spacebar' ? 'space' : key;
}

/** Keys whose browser default scrolling we suppress while the game has focus. */
const NAV_KEYS = new Set<string>(['arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'space']);

export class Input {
  private readonly held = new Set<string>();
  private readonly pressedThisStep = new Set<string>();
  private canvas: HTMLCanvasElement | null = null;

  private pointerX = 0;
  private pointerY = 0;
  private clickX = 0;
  private clickY = 0;
  private clickedThisStep = false;

  /** Begin listening for keyboard events on `window` and pointer on `canvas`. */
  attach(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mousedown', this.onMouseDown);
  }

  /** Stop listening and clear all state. */
  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    if (this.canvas) {
      this.canvas.removeEventListener('mousemove', this.onMouseMove);
      this.canvas.removeEventListener('mousedown', this.onMouseDown);
    }
    this.onBlur();
  }

  /** True while `key` is held down. */
  isDown(key: string): boolean {
    return this.held.has(key);
  }

  /** True if `key` transitioned to down during the current fixed step. */
  wasPressed(key: string): boolean {
    return this.pressedThisStep.has(key);
  }

  /** True if any key transitioned to down during the current fixed step. */
  anyPressed(): boolean {
    return this.pressedThisStep.size > 0;
  }

  /** Current pointer position in logical canvas pixels. */
  pointer(): PointerPos {
    return { x: this.pointerX, y: this.pointerY };
  }

  /** True if the pointer was pressed during the current fixed step. */
  wasClicked(): boolean {
    return this.clickedThisStep;
  }

  /** Position of the click that occurred this step (valid when `wasClicked`). */
  clickPos(): PointerPos {
    return { x: this.clickX, y: this.clickY };
  }

  /** Clear per-step edge state. Called by the engine after each fixed step. */
  endStep(): void {
    this.pressedThisStep.clear();
    this.clickedThisStep = false;
  }

  /** Press a key programmatically (test hook). */
  simulateKeyDown(raw: string): void {
    this.press(normalizeKey(raw));
  }

  /** Release a key programmatically (test hook). */
  simulateKeyUp(raw: string): void {
    this.held.delete(normalizeKey(raw));
  }

  private press(key: string): void {
    if (!this.held.has(key)) {
      this.pressedThisStep.add(key);
    }
    this.held.add(key);
  }

  /** Map a mouse event to logical canvas coordinates, accounting for CSS scale. */
  private toCanvas(event: MouseEvent): PointerPos {
    const canvas = this.canvas;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const sx = rect.width === 0 ? 1 : canvas.width / rect.width;
    const sy = rect.height === 0 ? 1 : canvas.height / rect.height;
    return { x: (event.clientX - rect.left) * sx, y: (event.clientY - rect.top) * sy };
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const key = normalizeKey(event.key);
    if (NAV_KEYS.has(key)) event.preventDefault();
    this.press(key);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.held.delete(normalizeKey(event.key));
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    const p = this.toCanvas(event);
    this.pointerX = p.x;
    this.pointerY = p.y;
  };

  private readonly onMouseDown = (event: MouseEvent): void => {
    const p = this.toCanvas(event);
    this.pointerX = p.x;
    this.pointerY = p.y;
    this.clickX = p.x;
    this.clickY = p.y;
    this.clickedThisStep = true;
  };

  private readonly onBlur = (): void => {
    this.held.clear();
    this.pressedThisStep.clear();
    this.clickedThisStep = false;
  };
}
